import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { EngagementRootPageDocument } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'

import subscriptionAddons from 'modules/billing/store/collections/subscriptionAddons'
import type SubscriptionAddon from 'modules/billing/store/models/SubscriptionAddon'
import CollectionsBootstrap from 'modules/engagement/lib/CollectionsBootstrap'
import PageLoading from 'modules/layout/components/Page/Loading'

export interface ContextProps {
  moduleAvailabilities: ModuleAvailabilityType[]
  enpsInstalled: boolean
  enpsSubscription: SubscriptionAddon | undefined
  surveysInstalled: boolean
  surveysSubscription: SubscriptionAddon | undefined
  upsellFlowEnabled: boolean
  loading: boolean
}

export const EngagementContext = React.createContext<ContextProps | null>(null)

const Container = () => {
  const { company } = useCurrentSession()
  const { loading: loadingModuleAvailabilities, moduleAvailabilities } = useModuleAvailabilities()
  const { loading: loadingSubcriptions } = useAsyncData(() => subscriptionAddons.fetch(), [])
  const loading = loadingModuleAvailabilities || loadingSubcriptions

  const enpsSubscription = subscriptionAddons.get('enps')
  const enpsInstalled = enpsSubscription?.attributes?.enabled ?? false

  const surveysSubscription = subscriptionAddons.get('surveys')
  const surveysInstalled = surveysSubscription?.attributes?.enabled ?? false
  const { data, loading: loadingFeatures } = useQuery(EngagementRootPageDocument, {
    variables: {
      companyId: company.id,
    },
  })
  const upsellFlowEnabled =
    data?.features.featuresConnection?.edges.find(
      (feature) => feature.node.name === 'engagement_upsell_flow'
    )?.node.enabled ?? false
  const canRequestUpsell = data?.upsell._policies.canRequestUpsell.can ?? false

  if (loading && !loadingFeatures) {
    return <PageLoading />
  }

  return (
    <EngagementContext.Provider
      value={{
        moduleAvailabilities,
        enpsInstalled,
        enpsSubscription,
        surveysInstalled,
        surveysSubscription,
        upsellFlowEnabled: upsellFlowEnabled && canRequestUpsell,
        loading,
      }}
    >
      <Outlet />
    </EngagementContext.Provider>
  )
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
